import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Brain, Check, ChevronDown, ChevronRight, Wrench, X } from 'lucide-react'
import type { TurnTrace } from './chatTraceStore'
import { activitySummary, isEmptyTrace, thinkingAutoExpanded } from './turnActivityState'

type Props = {
  trace: TurnTrace
  /** The turn is still producing. Drives the live timeline and auto-expansion. */
  isLive: boolean
  /** The turn has produced answer text. */
  hasAnswer: boolean
}

/** The reasoning row's id in the one-row-open model. Not a tool id. */
const THINKING_ROW = 'thinking'

/**
 * What the assistant did on its way to an answer: its reasoning, and every tool
 * it called, in the order they happened.
 *
 * Live it is a timeline that grows as events land, so a turn that takes ninety
 * seconds shows progress rather than an unchanging row of dots. Finished it
 * folds to a single chip, because by then the answer is the thing worth reading
 * and the trace is only there to be checked.
 */
const TurnActivity: FC<Props> = ({ trace, isLive, hasAnswer }) => {
  const intl = useIntl()
  const thinkingRef = useRef<HTMLPreElement | null>(null)

  /**
   * Null follows the default (open while live, collapsed once finished); a click
   * pins it. Same shape, and the same reason, as `ChatInner`'s
   * `patchOpenOverride`: the default must be free to track the turn without ever
   * fighting an explicit choice.
   */
  const [openOverride, setOpenOverride] = useState<boolean | null>(null)
  /** Which row's body is showing. One at a time keeps the timeline scannable. */
  const [rowOverride, setRowOverride] = useState<string | null>(null)
  const [rowPinned, setRowPinned] = useState(false)

  const hasThinking = !!trace.thinking
  const autoThinking = thinkingAutoExpanded({ isLive, hasThinking, hasAnswer })
  const openRow = rowPinned ? rowOverride : autoThinking ? THINKING_ROW : null
  const expanded = openOverride ?? isLive

  // Pin the reasoning pane to its newest line as deltas land. A layout effect so
  // it happens before paint and the text does not visibly jump.
  useLayoutEffect(() => {
    if (openRow !== THINKING_ROW) return
    const node = thinkingRef.current
    if (node) node.scrollTop = node.scrollHeight
  }, [openRow, trace.thinking])

  // A turn that finishes while the user has a row pinned open keeps it open; one
  // they never touched falls back to the default.
  useEffect(() => {
    if (!isLive) setRowPinned((pinned) => pinned && openRow !== null)
  }, [isLive, openRow])

  if (isEmptyTrace(trace)) return null

  const toggleRow = (id: string) => {
    setRowPinned(true)
    setRowOverride((current) => (current === id ? null : id))
  }

  const summary = activitySummary(trace)

  return (
    <div className='lum-activity'>
      <button
        type='button'
        data-testid='activity-summary'
        className='lum-activity-summary'
        aria-expanded={expanded}
        onClick={() => setOpenOverride(!expanded)}
      >
        {expanded ? (
          <ChevronDown size={13} aria-hidden='true' />
        ) : (
          <ChevronRight size={13} aria-hidden='true' />
        )}
        <span>{intl.formatMessage({ id: summary.id }, summary.values)}</span>
      </button>

      {expanded ? (
        <div className='lum-activity-steps'>
          {hasThinking ? (
            <div className='lum-activity-step'>
              <button
                type='button'
                data-testid='activity-row-thinking'
                className='lum-activity-row'
                aria-expanded={openRow === THINKING_ROW}
                onClick={() => toggleRow(THINKING_ROW)}
              >
                <Brain size={13} aria-hidden='true' className='lum-activity-icon' />
                <span className='lum-activity-label'>
                  {intl.formatMessage({
                    id: isLive && !hasAnswer ? 'CHAT.ACTIVITY.THINKING' : 'CHAT.ACTIVITY.THOUGHT',
                  })}
                </span>
              </button>
              {openRow === THINKING_ROW ? (
                /*
                  Model prose in the language of the turn, so it takes its
                  direction from itself. The tool-arguments `<pre>` below does
                  NOT: that one is JSON and stays left-to-right.
                */
                <pre className='lum-activity-thinking' ref={thinkingRef} dir='auto'>
                  {trace.thinking}
                </pre>
              ) : null}
            </div>
          ) : null}

          {trace.tools.map((tool) => (
            <div className='lum-activity-step' key={tool.id || tool.name}>
              <button
                type='button'
                data-testid={`activity-row-${tool.id || tool.name}`}
                className='lum-activity-row'
                aria-expanded={openRow === tool.id}
                onClick={() => toggleRow(tool.id)}
              >
                {tool.status === 'ok' ? (
                  <Check size={13} aria-hidden='true' className='lum-activity-icon is-ok' />
                ) : tool.status === 'error' ? (
                  <X size={13} aria-hidden='true' className='lum-activity-icon is-error' />
                ) : (
                  <Wrench size={13} aria-hidden='true' className='lum-activity-icon is-running' />
                )}
                <code className='lum-activity-tool'>{tool.name}</code>
                {tool.summary ? (
                  <span className='lum-activity-meta'>{tool.summary}</span>
                ) : tool.status === 'running' ? (
                  <span className='lum-activity-meta'>
                    {intl.formatMessage({ id: 'CHAT.ACTIVITY.RUNNING' })}
                  </span>
                ) : null}
              </button>
              {openRow === tool.id && tool.args ? (
                <pre className='lum-activity-args'>{JSON.stringify(tool.args, null, 2)}</pre>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default TurnActivity
